import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

export type TooltipPosition = 'top' | 'bottom'

export interface ButtonTooltipProps {
  children: React.ReactNode
  content?: string
  disabled?: boolean
  position?: TooltipPosition
  maxWidth?: number
}

function ButtonTooltip({
  children,
  content,
  disabled = false,
  position = 'top',
  maxWidth = 280,
}: ButtonTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipStyles, setTooltipStyles] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (isVisible && containerRef.current && tooltipRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const tooltipRect = tooltipRef.current.getBoundingClientRect()
      
      // Center the tooltip horizontally relative to the button
      const leftOffset = (containerRect.width - tooltipRect.width) / 2
      
      setTooltipStyles({
        left: `${leftOffset}px`,
      })
    }
  }, [isVisible, content])

  // Handle clicks on internal links to use React Router navigation
  const handleLinkClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.tagName === 'A') {
      const href = target.getAttribute('href')
      // Check if it's an internal link (starts with /)
      if (href && href.startsWith('/')) {
        e.preventDefault()
        navigate(href)
      }
    }
  }, [navigate])

  if (!content || disabled === false) {
    return <>{children}</>
  }

  return (
    <div
      ref={containerRef}
      className="relative inline-flex"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`
          absolute z-50 h-fit px-3 py-2 text-sm font-medium text-primary bg-gray-900 rounded-lg shadow-lg
          transition-all duration-200 ease-in-out whitespace-normal text-center
          ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'}
          ${isVisible ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible'}
          ${position === 'top' && !isVisible ? 'translate-y-1' : ''}
          ${position === 'bottom' && !isVisible ? '-translate-y-1' : ''}
        `}
        style={{
          maxWidth: `${maxWidth}px`,
          minWidth: '120px',
          ...tooltipStyles,
        }}
        role="tooltip"
      >
        <div 
          dangerouslySetInnerHTML={{ __html: content || '' }}
          className="[&_a]:underline [&_a]:text-primary [&_a]:hover:text-primary/80 [&_a]:cursor-pointer"
          onClick={handleLinkClick}
        />
        
        {/* Arrow */}
        <div
          className={`
            absolute left-1/2 -translate-x-1/2 w-0 h-0
            border-l-[6px] border-l-transparent
            border-r-[6px] border-r-transparent
            ${position === 'top' 
              ? 'top-full border-t-[6px] border-t-gray-900' 
              : 'bottom-full border-b-[6px] border-b-gray-900'
            }
          `}
        />
      </div>

      {/* Wrapped content (button) */}
      <div className={disabled ? 'cursor-not-allowed' : ''}>
        {children}
      </div>
    </div>
  )
}

export default ButtonTooltip
